import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useHistory } from "react-router-dom";

import styled from 'styled-components';
import * as PATH from '../../../lib/routes/path';
import { ERROR_CS } from '../../../lib/Error';
import ModalContext from '../../../modules/contexts/Modalcontext';
import PageTemplate from '../../../components/common/template/PageTemplate';
import { Text } from '../../../components/common/atom/Text';
import { Button } from '../../../components/common/atom/Button';
import { Flex } from '../../../components/common/atom/Layout';
import { breakpoint, SizeToRem } from '../../../lib/style/ThemeStyle';

import ThemeStyle from '../../../lib/style/ThemeStyle';
import { getCDNUrl } from '../../../lib/image/CDNUrl';
import { getUserAgentValue } from '../../../lib/util/UserAgent';
import { useShareableFile } from '../../../Hooks/useShareableFile';

const formatDateTime = (str) => str.replace('T', ' ').substring(0, str.length - 3);

const addHoursToISODate = (isoString, hoursToAdd) => {
  const date = new Date(isoString);
  date.setHours(date.getHours() + hoursToAdd);
  return date;
}

const HeFinalProductList = ({ workspace }) => {
  const history = useHistory();
  const { onModal } = useContext(ModalContext).actions;

  if (!workspace){
    alert(`잘못된 페이지 접근입니다.\n오류가 계속되면, ${ERROR_CS}`);
    history.push(PATH.HOME);
    return;
  }

  const isPrivacyConsented = workspace.isPrivacyConsented;
  const isReviewEventApplied = (workspace.reviewEvent !== null);
  
  const [ step, setStep ] = useState(isReviewEventApplied ? 'list' : 'event'); // 'event', 'list'

  const finalProducts = 
    workspace.croppedFaces ? workspace.croppedFaces.map(croppedFace => croppedFace.finalProducts).flat() : [];

  const clearfaces = 
    finalProducts.filter(f => f.originalProductName === "Clearface" && f.productName !== "Motion Video");

  const babyfaces = 
    finalProducts.filter(f => (f.originalProductName === "Clearface" || f.originalProductName === "Babyface") && f.productName !== "Motion Video");

  const isBabyfaceAvailable = 
    finalProducts.some(f => f.productName === "Babyface" || f.originalProductName === "Babyface");

  const isAnalyzing = (!isBabyfaceAvailable && clearfaces.length === 0) || (isBabyfaceAvailable && babyfaces.length === 0);

  const PAGE_TITLE_PHOTO = isBabyfaceAvailable ? PAGE_TITLE_BABYFACE : PAGE_TITLE_CLEARFACE;
  const framedPhotos = isBabyfaceAvailable ? babyfaces : clearfaces;
  
  const motionVideos = 
    finalProducts.filter(f => f.productName === "Motion Video");

  const filteredFinalProducts =
    [...framedPhotos, ...motionVideos].filter(f => f.resultDownloadUrl !== null);

  const motionVideo = motionVideos.filter(f => f.resultDownloadUrl !== null)[0];

  const { isKakaoInApp } = getUserAgentValue();
  const motionVideoFile = useShareableFile(
    motionVideo && !isKakaoInApp ? motionVideo.resultDownloadUrl : null, '베이비페이스_배냇짓.mp4');

  // 카카오톡 인앱 브라우저는 파일 공유를 지원하지 않아 외부 브라우저로 전환시킨다
  const openInExternalBrowser = useCallback(() => {
    window.location.href =
      `kakaotalk://web/openExternal?url=${encodeURIComponent(window.location.href)}`;
  }, []);

  // 공유 시트를 취소하면 AbortError로 reject되므로, 완료한 경우에만 안내한다
  const saveMotionVideoToPhotos = useCallback(() => {
    navigator.share({ files: [motionVideoFile] })
      .then(() => onModal({
        title: '저장 완료',
        content: <ModalText>사진 앱에서 동영상을 확인해 주세요.</ModalText>,
      }))
      .catch(() => {});
  }, [motionVideoFile, onModal]);

  const couponExpirationDate = clearfaces && clearfaces.length > 0 ? addHoursToISODate(clearfaces[clearfaces.length - 1].createDateTime, 3) : new Date(1970, 1, 1, 0, 0, 0);

  // profit_share에 따른 할인 정보 계산
  const getDiscountInfo = () => {
    const profitShare = sessionStorage.getItem('profit_share');
    if (profitShare) {
      const isProfitShare = profitShare === 'true';
      if (isProfitShare) {
        return {
          amount: '2,500원',
          type: '무료 배송 쿠폰',
          description: '배송비 2,500원 할인'
        };
      } else {
        return {
          amount: '4,500원',
          type: '빠른 분석 쿠폰',
          description: '빠른 분석 4,500원 할인'
        };
      }
    }
    // profit_share가 없으면 기존 로직
    const amount = workspace.hospital.id === 110 ? '2,000원' : '7,000원';
    return {
      amount,
      type: '베이비페이스 할인쿠폰',
      description: `${amount} 할인`
    };
  };

  const discountInfo = getDiscountInfo();

  // 세션에서 쿠폰 코드 배열 가져오기 및 설정 헬퍼 함수
  const getPreSelectedVoucherCodes = () => {
    const preSelectedVoucherCodes = sessionStorage.preSelectedVoucherCodes;
    if (preSelectedVoucherCodes) {
      try {
        return JSON.parse(preSelectedVoucherCodes);
      } catch (e) {
        return [];
      }
    }
    // 기존 단일 문자열 형식 호환
    const preSelectedVoucherCode = sessionStorage.preSelectedVoucherCode;
    return preSelectedVoucherCode ? [preSelectedVoucherCode] : [];
  };

  const addPreSelectedVoucherCode = (newCode) => {
    const existingCodes = getPreSelectedVoucherCodes();
    if (!existingCodes.includes(newCode)) {
      const updatedCodes = [...existingCodes, newCode];
      sessionStorage.setItem('preSelectedVoucherCodes', JSON.stringify(updatedCodes));
      // 기존 단일 문자열도 첫 번째 쿠폰으로 유지 (호환성)
      sessionStorage.setItem('preSelectedVoucherCode', updatedCodes[0]);
    }
  };

  // 클리어페이스 이미지 확인 시 profit_share에 따른 쿠폰 발급
  useEffect(() => {
    const utmCampaign = sessionStorage.getItem('utm_campaign');
    const profitShare = sessionStorage.getItem('profit_share');
    
    console.log('HE 쿠폰 발급 체크:', {
      utmCampaign,
      profitShare,
      clearfacesLength: clearfaces && clearfaces.length,
      hospitalId: workspace && workspace.hospital && workspace.hospital.id
    });
    
    // profit_share가 있으면 쿠폰 발급 (clearfacedownload 캠페인 또는 profit_share 파라미터가 있을 때)
    if (profitShare && clearfaces && clearfaces.length > 0) {
      const isProfitShare = profitShare === 'true';
      const hospitalId = workspace.hospital.id;
      const couponCode = isProfitShare 
        ? `HE_DISCOUNT_${hospitalId}_SHIPPING`  // 무료 배송 쿠폰 (배송비 2500원 할인)
        : `HE_DISCOUNT_${hospitalId}_FAST`;      // 빠른 분석 쿠폰 (4,500원 할인)
      
      console.log('HE 쿠폰 발급:', couponCode);
      addPreSelectedVoucherCode(couponCode);
      sessionStorage.setItem('he_hospital_name', workspace.hospital.name);
    } else {
      console.log('HE 쿠폰 발급 조건 불만족:', {
        hasProfitShare: !!profitShare,
        hasClearfaces: !!clearfaces,
        clearfacesLength: clearfaces && clearfaces.length
      });
    }
  }, [clearfaces, workspace]); 

  const handleListClick = useCallback(() => {
    setStep('list');
    window.scrollTo(0, 0);
  }, []);

  const handleSalesClick = () => {
    sessionStorage.setItem('be_ultrasound', clearfaces[clearfaces.length - 1].originalImageUrl);
    sessionStorage.setItem('be_hospital', workspace.hospital.name);
    if (new Date() <= couponExpirationDate) {
      // profit_share에 따른 쿠폰 코드 결정
      const profitShare = sessionStorage.getItem('profit_share');
      const hospitalId = workspace.hospital.id;
      let couponCode;
      
      if (profitShare) {
        const isProfitShare = profitShare === 'true';
        couponCode = isProfitShare 
          ? `HE_DISCOUNT_${hospitalId}_SHIPPING`  // 무료 배송 쿠폰 (배송비 2500원 할인)
          : `HE_DISCOUNT_${hospitalId}_FAST`;      // 빠른 분석 쿠폰 (4,500원 할인)
      } else {
        // profit_share가 없으면 기존 로직 사용
        couponCode = workspace.hospital.id === 110 ? '24_07_HE_DISCOUNT' : HE_COUPON_CODE;
      }
      
      addPreSelectedVoucherCode(couponCode);
      sessionStorage.setItem('he_hospital_name', workspace.hospital.name);
      onModal({
        title: '쿠폰 저장 완료',
        content: <ModalText>쿠폰은 안전하게 저장되었습니다.<br />주문 페이지에서 쿠폰이 자동으로 적용됩니다.</ModalText>,
        afterConfirmEvent: () => history.push(PATH.SALES),
      });
    } else {
      sessionStorage.removeItem('preSelectedVoucherCode');
      history.push(PATH.SALES);
    }
  }

  const handleReviewEventClick = () => {
    if (!isPrivacyConsented) {
      alert("개인정보 활용 동의 후 참여 가능합니다.");
      history.push({
        pathname: `${PATH.HE_RESULTS}/${workspace.id}`,
      });
    } else {
      history.push({
        pathname: PATH.HE_REVIEW_EVENT,
        state: { workspace },
      });
    }
  }

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isAnalyzing]);

  if (isAnalyzing) {
    return (
      <PageTemplate title={PAGE_TITLE_ANALYZING}>
        <div style={{ marginBottom: '1.5rem' }}>
          <Text size={16} lineHeight={1.5}>
            방사선사 선생님이 정성을 다해 태아의 입체초음파 영상을 분석하고 있어요.<br /><br />
            조금만 더 기다려 주시면 아기의 생생한 모습을 만나보실 수 있습니다.
          </Text>
        </div>
        <RefreshButton
          style={{ height: '2.595rem', marginTop: '1.25rem' }}
          onClick={() => window.location.reload()}
        >
          {REFRESH_BUTTON}
        </RefreshButton>
      </PageTemplate>
    );
  } else if (step === "event") {
    return (
      <PageTemplate title={PAGE_TITLE_PHOTO} noMargin={true}>
        {isBabyfaceAvailable ? 
          <>
            <div style={{ margin: SizeToRem(20), marginBottom: '1.5rem' }}>
              <p>
                <Text size={20} weight="bold" lineHeight={1.5}>잠깐! 놓치면 후회할 이벤트가 있어요</Text><br />
                <Text size={16} lineHeight={1.5}>이미지 다운로드 전에 꼭 확인해 보세요</Text>
              </p>
              <EventInfo>
                <LimitedText>🔥 LIMITED EVENT</LimitedText>
                <p><Text size={20} weight="bold" lineHeight={1.5}><b style={{color: 'black'}}>내 새끼 자랑 이벤트</b></Text></p>
                <p><Text size={20} weight="bold" lineHeight={1.5}><b>배냇짓 동영상 무료 제공!</b></Text></p>
                <p><Text size={16} lineHeight={1.5}>병원 후기 작성만 하면 즉시 제공</Text></p>

                <video autoPlay loop muted playsInline style={{ width: '100%', display: 'block', margin: '1.5rem 0' }}>
                  <source src={getCDNUrl("service", "babyface_he_video_1.mp4")} type="video/mp4" />
                </video>  
                <p><Text size={16} lineHeight={1.5}>우리 아기의 특별한 순간을 놓치지 마세요<br />
                소중한 추억을 동영상으로 간직하세요 💕</Text></p>
              </EventInfo>
            </div> 
            <ButtonBox>
              <BabyfaceButton
                onClick={handleReviewEventClick}>
                <p>🎬 배넷짓 동영상 받기<br /><Text weight="regular" size={12}>후기 작성하고 무료로 받아가세요!</Text></p>
              </BabyfaceButton>
              <OutlineButton
                onClick={handleListClick}>
                나중에 할게요, 이미지만 받을게요
              </OutlineButton>
              <Text size={12} style={{ marginTop: '0.5rem', color: ThemeStyle.color.brownGrey }}>이벤트는 언제든 종료될 수 있습니다</Text>
            </ButtonBox>
          </>
          :
          <>
            <div style={{ margin: SizeToRem(20), marginBottom: '1.5rem', textAlign: 'center'}}>
              <p>
                <Text size={20} weight="bold" lineHeight={1.5}>🎊 클리어페이스 결과가 완성되었어요!</Text><br />
                <Text size={16} weight="bold" lineHeight={1.5}><b>잠깐! 💝 특별 선물을 준비했어요!</b></Text>
              </p>
              <div style={{margin: '1.5rem 0', padding: '0.5rem', background: '#fdf2f8', borderRadius: '1rem', border: '1px solid rgb(254, 202, 202)'}}>
                <p style={{margin: '0.5rem'}}><Text size={20} weight="bold" lineHeight={1.5}>🎁 {discountInfo.type}</Text></p>
                <p style={{margin: '0.5rem'}}><Text size={24} weight="bold" style={{color: 'rgb(220, 38, 38)'}}>{discountInfo.amount} 할인</Text></p>
                <p style={{margin: '0.5rem'}}><Text size={14} lineHeight={1.5} style={{color: 'rgb(107, 114, 128)'}}>{discountInfo.description}</Text></p>
                <p style={{margin: '0.5rem'}}><Text size={14} weight="bold" lineHeight={1.5} style={{color: 'rgb(37, 99, 235)'}}>우리 병원 고객에게만 드리는 특별 혜택</Text><br />
                <Text size={14} lineHeight={1.5} style={{color: 'rgb(22, 163, 74)'}}>이 페이지에서 주문 시 자동 할인 적용</Text></p>
              </div>
              <p style={{margin: '1rem'}}><Text size={20} weight="bold">"우리 아이, 보고 싶었어"</Text></p>
              <Image src={getCDNUrl("service", "babyface_he_img_2.png")} alt="베이비페이스 이미지" />
                <p><Text size={14} lineHeight={1.5}>아기의 태어날 모습을 미리 만나보세요!<br />
                베이비페이스로 소중한 순간을 간직하세요.</Text></p>      
            </div>
            <ButtonBox>
              <BabyfaceButton
                onClick={handleSalesClick}>
                <p>우리 아기 보러 가기<br /><Text weight="regular" size={12}>⚡ 특별 할인이 자동으로 적용됩니다!</Text></p>
              </BabyfaceButton>
              <OutlineButton
                onClick={handleListClick}>
                클리어페이스 이미지 받기
              </OutlineButton>
            </ButtonBox>
          </>
        }
      </PageTemplate>
    );
  } else if (step === "list") {
    return (
      <PageTemplate title={PAGE_TITLE_PHOTO} noMargin={true}>
        <div style={{ margin: SizeToRem(20) }}>
          <Text size={20} weight="bold" lineHeight={1.5}>이미지 다운로드<br /></Text>
          <Text size={16} lineHeight={1.5}>아기의 선명한 모습을 확인해보세요!</Text>
          <div style={{ padding: '0.5rem' }}>
            {filteredFinalProducts.map((finalProduct) => {
              const productName = finalProduct.productName === "Motion Video" ? "베이비페이스 베냇짓 영상" :
                ((finalProduct.originalProductName === "Clearface" || finalProduct.productName === "Clearface") ? "클리어페이스 이미지" : 
                ((finalProduct.originalProductName === "Babyface" || finalProduct.productName === "Babyface") ? "베이비페이스 이미지" :
                (finalProduct.originalProductName || finalProduct.productName)))

              const isMotionVideo = finalProduct.productName === "Motion Video";

              // 인앱 브라우저는 파일 공유가 막혀 있다. 기존 재생 링크를 그대로 두고
              // 사파리로 넘어갈 수 있는 길만 덧붙여, 스킴이 안 먹어도 후퇴하지 않게 한다
              if (isMotionVideo && isKakaoInApp) {
                return (
                  <FinalProductItem key={finalProduct.id}>
                    <a href={finalProduct.resultDownloadUrl}>
                      <Text size={16} lineHeight={1.75}>{formatDateTime(finalProduct.createDateTime)}</Text><br />
                      <Text size={16} weight="bold" lineHeight={1.75}>{productName} 다운로드</Text>
                    </a><br />
                    <Text size={16} color="orangePink" lineHeight={1.5} onClick={openInExternalBrowser}>
                      동영상 다운로드 버튼이 안보이시면<br />
                      여기를 클릭해서 사파리에서 열기
                    </Text>
                  </FinalProductItem>
                  )
              }

              if (isMotionVideo && motionVideoFile) {
                return (
                  <FinalProductItem key={finalProduct.id} onClick={saveMotionVideoToPhotos}>
                    <Text size={16} lineHeight={1.75}>{formatDateTime(finalProduct.createDateTime)}</Text><br />
                    <Text size={16} weight="bold" lineHeight={1.75}>{productName} 갤러리에 저장</Text>
                  </FinalProductItem>
                  )
              }

              return (
                <a
                  href={finalProduct.resultDownloadUrl}
                  key={finalProduct.id}
                >
                  <FinalProductItem>
                    <Text size={16} lineHeight={1.75}>{formatDateTime(finalProduct.createDateTime)}</Text><br />
                    <Text size={16} weight="bold" lineHeight={1.75}>{productName} 다운로드</Text>
                  </FinalProductItem>
                </a>
                )
              }
            )}

          </div>
          {isBabyfaceAvailable ? 
            !isReviewEventApplied ?
              <EventInfo style={{ margin: '1rem 0 2rem' }}>
                <p><Text size={20} weight="bold" lineHeight={1.5}>🎁 놓치면 안 되는 특별혜택!</Text><br /></p>
                <EventContainer>
                  <p><Text size={20} weight="bold" lineHeight={1.5}><b style={{color: 'black'}}>내 새끼 자랑 이벤트</b></Text></p>
                  <p><Text size={16} weight="bold" lineHeight={1.5}><b>배냇짓 동영상 무료 제공!</b></Text></p>
                  <p><Text size={16} lineHeight={1.5}>병원 후기 작성만 하면 즉시 제공</Text></p>

                  <video autoPlay loop muted playsInline style={{ width: '100%', display: 'block', margin: '1.5rem 0' }}>
                    <source src={getCDNUrl("service", "babyface_he_video_1.mp4")} type="video/mp4" />
                  </video>
                </EventContainer>
                <p><Text size={14} lineHeight={1.5}>우리 아기가 움직이는 모습이 궁금하시다면?<br />
                <span style={{fontSize: 'calc(14rem / 16)', color: ThemeStyle.color.orangePink}}>배냇짓 동영상</span>으로 더 특별한 추억을 만들어보세요</Text></p>

              </EventInfo>
              :
              <></>
          :
            <EventInfo style={{ margin: '1rem 0 2rem' }}>
              <p><Text size={20} weight="bold" lineHeight={1.5}>🎁 놓치면 안 되는 특별혜택!</Text><br /></p>
              <EventContainer style={{margin: '1.5rem 0', padding: '1rem 0'}}>
                <p style={{margin: '0rem'}}><Text size={18} weight="bold">{discountInfo.type}</Text></p>
                <p style={{margin: '0rem'}}><Text size={16} weight="bold" style={{color: 'rgb(220, 38, 38)'}}>{discountInfo.amount} 할인</Text></p>
                <p style={{margin: '0rem'}}><Text size={14} lineHeight={1.5} style={{color: 'rgb(107, 114, 128)'}}>{discountInfo.description}</Text></p>
                <p style={{margin: '0rem'}}><Text size={14} weight="bold" style={{color: 'rgb(37, 99, 235)'}}>우리 병원 고객에게만 드리는 특별 혜택</Text></p>
              </EventContainer>
              <p style={{textAlign: 'center'}}><Text size={14} lineHeight={1.5}>태어날 아기의 실제 모습이 궁금하다면?</Text></p>
            </EventInfo>
          }
        </div>
        {isBabyfaceAvailable ? 
          <ButtonBox>
            {!isReviewEventApplied &&
              <BabyfaceButton
                onClick={handleReviewEventClick}>
                <p>🎬 배넷짓 동영상 받기<br /><Text weight="regular" size={12} >후기 작성하고 무료로 받아가세요!</Text></p>
              </BabyfaceButton>
            }
            <Text size={12} style={{ marginTop: '0.5rem', color: ThemeStyle.color.brownGrey, textAlign: 
            'center'
              }}>베이비페이스를 이용해 주셔서 감사합니다 💕<br />소중한 추억이 되길 바랍니다</Text>
          </ButtonBox>
          :
          <ButtonBox>
            <BabyfaceButton
              onClick={handleSalesClick}>
              <p>우리 아기 보러 가기<br /><Text weight="regular" size={12}>⚡ 특별 할인이 자동으로 적용됩니다!</Text></p>
            </BabyfaceButton>
          </ButtonBox>
          }
      </PageTemplate>
    );
  }
}

export default React.memo(HeFinalProductList);

const PAGE_TITLE_ANALYZING = '클리어페이스 분석 중';
const PAGE_TITLE_CLEARFACE = '클리어페이스';
const PAGE_TITLE_BABYFACE = '베이비페이스';
const REFRESH_BUTTON = '클리어페이스 확인하기';

export const HE_COUPON_CODE = '24_07_HE_DISCOUNT';

const FinalProductItem = styled.div`
  border: 1px solid #C7C7C7;
  border-radius: 5px;
  width: 19.5rem;
  padding: 1rem;
  margin: 0.625rem auto;
  cursor: pointer;
`

const Image = styled.img`
  width: 100%;
`

const ChanceInfo = styled.div`
  padding: 7.5px 15px;
  border: 2px solid #ff6452;
  border-radius: 5px;
  font-size: 0.75rem;
  font-weight: bold;
  line-height: 1.5;
`

const Right = styled.div`
  float: right;
  font-size: 0.75rem;
  font-weight: bold;
  line-height: 1.5;
`

const RefreshButton = styled(Button)`
  border-radius: 0.375rem;
  background: ${(props) => props.theme.color.orangePink};
`;

const LimitedText = styled.div`
  height: 1.5rem;
  background-color: oklch(63.7% 0.237 25.331);
  border-radius: 1rem;
  padding: 0 0.75rem;
  margin-bottom: 1rem;
  display: inline-block;
  -webkit-align-content: center;
  -ms-flex-line-pack: center;
  align-content: center;
  color: white;
  font-size: 0.75rem;
  font-weight: bold;
`

const EventInfo = styled.div`
  margin-bottom: 1.5rem;
  padding: 1.25rem;
  background-color: rgb(239, 246, 255);
  border: 1px solid rgb(219, 234, 254);
  border-radius: 0.75rem;
  line-height: 1.25;
  text-align: center;
  p {
    margin: 0.5rem;
  }
`;

const EventContainer = styled.div`
  margin: 1.5rem 0;
  padding: 0.75rem;
  background-color: white;
  border: 1px solid rgb(191 219 254);
  border-radius: 0.75rem;
  line-height: 1.25;
  text-align: center;
  p {
    margin: 0.5rem;
  }
`;

export const ButtonBox = styled(Flex)`
  position: fixed;
  flex-direction: column ;
  bottom: 0;
  width: 100%;
  max-width: ${breakpoint}px;
  padding: 1rem;
  background: #ffffff;
  box-shadow: 0 -3px 6px 0 rgba(0, 0, 0, 0.16);
  z-index: 20;
  text-align: center;
`;

const BabyfaceButton = styled(Button)`
  position: relative;
  height: 4rem;
  border-radius: 0.375rem;
  border: none;
  padding: 1rem;
  background: ${(props) => props.theme.color.orangePink};
  color: ${(props) => props.theme.color.white};
  text-align: center;
  span {
    font-weight: normal;
  }
`;

const OutlineButton = styled(Button)`
  height: 2.75rem;
  margin-top: 0.5rem;
  border-radius: 0.375rem;
  border: ${(props) => `1px solid ${props.theme.color.orangePink}`};
  padding: 1rem;
  background: ${(props) => props.theme.color.background};
  color: ${(props) => props.theme.color.orangePink};
`;

const ModalText = styled(Text)`
  text-align: center;
  line-height: 1.5;
  white-space: pre-line;
`;
